import { useEffect, useState } from "react";
import api, { API, formatApiErrorDetail } from "../../lib/api";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Checkbox } from "../ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "../ui/dialog";
import { Loader2, Receipt } from "lucide-react";
import { toast } from "sonner";

const fmtEur = (v) =>
  new Intl.NumberFormat("es-ES", { style: "currency", currency: "EUR" }).format(Number(v) || 0);

const downloadBlob = async (url, filename, opts = {}) => {
  const token = localStorage.getItem("token");
  const res = await fetch(url, {
    ...opts,
    headers: { ...(opts.headers || {}), Authorization: `Bearer ${token}` },
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.detail || `HTTP ${res.status}`);
  }
  const blob = await res.blob();
  const objectUrl = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = objectUrl;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(objectUrl);
  return res;
};

/**
 * Prices a selection of course ids through the same engine the public shop
 * uses (packages → per-course discounts → bulk → coupon) and shows the
 * breakdown.
 *
 * `informational` renders the "assigning charges nothing" note. When `userId`
 * is given it also offers two ways to charge the computed total: a plain
 * ticket ("Generar pago") and a Sabadell Consumer one, whose only difference
 * is that the concept is prefixed with SABADELL — that is how financed sales
 * are told apart in the receipts listing.
 */
export default function CartPricingSummary({
  courseIds,
  informational = false,
  userId = null,
  onPaid = null,
}) {
  const [cart, setCart] = useState(null);
  const [payOpen, setPayOpen] = useState(false);
  const [payKind, setPayKind] = useState("normal"); // "normal" | "sabadell"
  const [concept, setConcept] = useState("");
  const [alsoAssign, setAlsoAssign] = useState(true);
  const [saving, setSaving] = useState(false);
  // "Código DTO": typed freely, only sent to the server once applied.
  const [codeInput, setCodeInput] = useState("");
  const [code, setCode] = useState("");

  useEffect(() => {
    if (!courseIds?.length) {
      setCart(null);
      return;
    }
    let cancelled = false;
    api
      .post("/admin/pricing/preview", {
        course_ids: courseIds,
        discount_code: code || null,
        user_id: userId,
      })
      .then(({ data }) => { if (!cancelled) setCart(data); })
      .catch(() => { if (!cancelled) setCart(null); });
    return () => { cancelled = true; };
  }, [courseIds, code, userId]);

  if (!courseIds?.length || !cart) return null;

  const packages = cart.applied_packages || [];
  const lines = cart.lines || [];
  const bulk = cart.bulk_applied;
  const codeApplied = cart.code_applied;
  const codeError = cart.code_error;
  const savings = Number(cart.discount_total) || 0;
  const total = Number(cart.total) || 0;

  // Default concept: the packs and courses actually being paid for.
  const defaultConcept = [
    ...packages.map((p) => p.promo?.name || "Paquete"),
    ...lines.map((l) => l.title),
  ].join(", ");

  const openPay = (kind) => {
    setPayKind(kind);
    // A Sabadell sale is the same ticket with the financier in front of the
    // course name, so it can be spotted at a glance in the receipts listing.
    setConcept(kind === "sabadell" ? `SABADELL ${defaultConcept}` : defaultConcept);
    setAlsoAssign(true);
    setPayOpen(true);
  };

  const submitPay = async () => {
    if (!concept.trim()) {
      toast.error("El concepto es obligatorio");
      return;
    }
    setSaving(true);
    try {
      if (alsoAssign) {
        // Idempotent server-side: courses already assigned are skipped.
        await api.post("/admin/assignments/bulk", {
          user_id: userId,
          course_ids: courseIds,
        });
      }
      const res = await downloadBlob(
        `${API}/admin/users/${userId}/manual-payment`,
        "ticket.pdf",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            amount: total,
            concept: concept.trim(),
            course_ids: courseIds,
            discount_code: code || null,
          }),
        },
      );
      const num = res.headers.get("X-Ticket-Number");
      toast.success(`Recibo ${num || ""} emitido${alsoAssign ? " y cursos asignados" : ""}`);
      setPayOpen(false);
      onPaid?.();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.message) || "No se pudo generar el pago");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="mt-2 rounded-md border border-gray-200 bg-white p-3 text-xs space-y-1.5"
      data-testid="cart-pricing-summary"
    >
      {packages.map((p) => (
        <div key={p.promo?.id || p.promo?.name} className="flex justify-between gap-2">
          <span className="text-purple-800">
            📦 {p.promo?.name || "Paquete"}
            <span className="text-gray-400"> ({(p.course_ids || []).length} cursos)</span>
          </span>
          <span className="font-mono-data">{fmtEur(p.fixed_price)}</span>
        </div>
      ))}
      {lines.map((ln) => (
        <div key={ln.course_id} className="flex justify-between gap-2">
          <span className="text-gray-700 truncate">
            {ln.title}
            {ln.discount > 0 && (
              <span className="text-emerald-700"> · −{fmtEur(ln.discount)}</span>
            )}
          </span>
          <span className="font-mono-data">{fmtEur(ln.final_price)}</span>
        </div>
      ))}
      {bulk && (
        <div className="flex justify-between gap-2 text-emerald-700">
          <span>Descuento por lote ({bulk.percent}%)</span>
          <span className="font-mono-data">−{fmtEur(bulk.amount)}</span>
        </div>
      )}
      {codeApplied && (
        <div className="flex justify-between gap-2 text-emerald-700">
          <span>Código {codeApplied.code}</span>
          <span className="font-mono-data">−{fmtEur(codeApplied.amount)}</span>
        </div>
      )}
      <div className="flex justify-between gap-2 border-t border-gray-200 pt-1.5 font-semibold text-brand-ink">
        <span>Total{savings > 0 ? ` (ahorro ${fmtEur(savings)})` : ""}</span>
        <span className="font-mono-data" data-testid="cart-pricing-total">{fmtEur(total)}</span>
      </div>

      <div className="flex items-end gap-2 pt-1.5">
        <div className="flex-1 max-w-44">
          <Label htmlFor="cart-code" className="text-[10px] uppercase tracking-wide text-gray-500">
            Código DTO
          </Label>
          <div className="flex gap-1">
            <Input
              id="cart-code"
              value={codeInput}
              onChange={(e) => setCodeInput(e.target.value.toUpperCase())}
              onKeyDown={(e) => e.key === "Enter" && setCode(codeInput.trim())}
              placeholder="Opcional"
              className="h-8 text-xs font-mono-data"
              data-testid="cart-discount-code"
            />
            {code ? (
              <Button
                size="sm"
                variant="ghost"
                className="h-8 px-2 text-gray-500"
                onClick={() => { setCode(""); setCodeInput(""); }}
                data-testid="cart-discount-code-clear"
              >
                Quitar
              </Button>
            ) : (
              <Button
                size="sm"
                variant="outline"
                className="h-8 px-2"
                disabled={!codeInput.trim()}
                onClick={() => setCode(codeInput.trim())}
                data-testid="cart-discount-code-apply"
              >
                Aplicar
              </Button>
            )}
          </div>
        </div>
      </div>
      {codeError && (
        <p className="text-[11px] text-red-600" data-testid="cart-discount-code-error">{codeError}</p>
      )}

      {userId && total > 0 && (
        <div className="flex flex-wrap items-center gap-2 pt-1.5">
          <Button
            size="sm"
            variant="outline"
            onClick={() => openPay("normal")}
            className="border-brand text-brand-ink hover:bg-brand-soft"
            data-testid="generate-payment-button"
          >
            <Receipt className="w-3.5 h-3.5 mr-1.5" />
            Generar pago ({fmtEur(total)})
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={() => openPay("sabadell")}
            className="border-[#0071FF] bg-white hover:bg-[#eaf3ff] px-2 h-8"
            title={`Generar ticket SABADELL por ${fmtEur(total)}`}
            data-testid="generate-payment-sabadell"
          >
            <img
              src="/sabadell-consumer.svg"
              alt="Sabadell Consumer"
              className="h-5 w-auto"
              onError={(e) => { e.currentTarget.style.display = "none"; }}
            />
            <span className="ml-1.5 text-[#0071FF] font-semibold">{fmtEur(total)}</span>
          </Button>
        </div>
      )}

      {informational && (
        <p className="text-[10px] text-gray-500 pt-0.5">
          Asignar cursos no genera ningún cobro. Usa &quot;Generar pago&quot; si además quieres
          registrar el cobro y emitir el recibo.
        </p>
      )}

      <Dialog open={payOpen} onOpenChange={(o) => !o && setPayOpen(false)}>
        <DialogContent data-testid="generate-payment-dialog">
          <DialogHeader>
            <DialogTitle>
              {payKind === "sabadell"
                ? `Generar pago SABADELL de ${fmtEur(total)}`
                : `Generar pago de ${fmtEur(total)}`}
            </DialogTitle>
            <DialogDescription>
              Registra el cobro y emite un recibo con numeración correlativa. El PDF se descargará
              automáticamente.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label htmlFor="pay-concept" className="text-sm">Concepto del recibo</Label>
              <Input
                id="pay-concept"
                value={concept}
                onChange={(e) => setConcept(e.target.value)}
                data-testid="generate-payment-concept"
              />
              <p className="text-[11px] text-gray-500 mt-1">Es el texto que aparecerá en el PDF.</p>
            </div>
            <label className="flex items-start gap-2 cursor-pointer">
              <Checkbox
                checked={alsoAssign}
                onCheckedChange={(v) => setAlsoAssign(!!v)}
                className="mt-0.5"
                data-testid="generate-payment-assign"
              />
              <span className="text-sm text-gray-700">
                Asignar también los cursos seleccionados
                <span className="block text-[11px] text-gray-500">
                  Los que ya tenga asignados se omiten.
                </span>
              </span>
            </label>
            <p className="text-[11px] text-amber-900 bg-amber-50 border border-amber-200 rounded-md p-2">
              El importe es el total con las ofertas ya aplicadas. Esto no cobra nada por la pasarela:
              registra un pago recibido por otro medio.
            </p>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setPayOpen(false)}>Cancelar</Button>
            <Button
              onClick={submitPay}
              disabled={saving}
              className="bg-brand hover:bg-brand-hover text-white"
              data-testid="generate-payment-confirm"
            >
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Receipt className="w-4 h-4 mr-2" />}
              Generar recibo
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
